/**
 * Organization Branding Service
 * Handles all branding-related operations including logo uploads
 */

import { supabase } from "@foundation/utils/supabaseClient";
import {
  OrganizationBranding,
  CreateBrandingData,
  UpdateBrandingData,
  LogoUploadResult,
} from "@foundation/types/organization";
import { BRANDING_CONFIG } from "@foundation/utils/constants";

// Constants for branding service
const BRANDING_BUCKET = BRANDING_CONFIG.LOGO.BUCKET_NAME;
const MAX_LOGO_SIZE = BRANDING_CONFIG.LOGO.MAX_SIZE;
const ALLOWED_LOGO_TYPES = BRANDING_CONFIG.LOGO.ALLOWED_TYPES;

export class BrandingService {
  /**
   * Get branding for an organization
   */
  static async getOrganizationBranding(
    organizationId: string
  ): Promise<OrganizationBranding | null> {
    try {
      // Use maybeSingle() so Supabase returns null cleanly (no HTTP error)
      // when no branding row exists, instead of throwing PGRST116.
      const { data, error } = await supabase
        .from("base_organisation_branding")
        .select("*")
        .eq("organisation_id", organizationId)
        .maybeSingle();

      if (error) throw error;

      return data;
    } catch (error) {
      console.error("Error fetching organization branding:", error);
      throw error;
    }
  }

  /**
   * Create new branding for an organization
   */
  static async createOrganizationBranding(
    brandingData: CreateBrandingData
  ): Promise<OrganizationBranding> {
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) {
        throw new Error("User not authenticated");
      }

      const { data, error } = await supabase
        .from("base_organisation_branding")
        .insert({
          ...brandingData,
          created_by: user.id,
          updated_at: new Date().toISOString(),
        })
        .select()
        .single();

      if (error) {
        throw error;
      }

      return data;
    } catch (error) {
      console.error("Error creating organization branding:", error);
      throw error;
    }
  }

  /**
   * Update existing branding for an organization
   */
  static async updateOrganizationBranding(
    brandingData: UpdateBrandingData
  ): Promise<OrganizationBranding> {
    try {
      const { id, ...updateData } = brandingData;

      const { data, error } = await supabase
        .from("base_organisation_branding")
        .update({
          ...updateData,
          updated_at: new Date().toISOString(),
        })
        .eq("id", id)
        .select()
        .single();

      if (error) {
        throw error;
      }

      return data;
    } catch (error) {
      console.error("Error updating organization branding:", error);
      throw error;
    }
  }

  /**
   * Delete branding for an organization
   */
  static async deleteOrganizationBranding(brandingId: string): Promise<void> {
    try {
      // First get the branding to check if there's a logo to delete
      const { data: branding } = await supabase
        .from("base_organisation_branding")
        .select("logo_url, organisation_id")
        .eq("id", brandingId)
        .single();

      // Delete logo from storage if exists
      if (branding?.logo_url) {
        await this.deleteLogoFromStorage(branding.organisation_id);
      }

      // Delete branding record
      const { error } = await supabase
        .from("base_organisation_branding")
        .delete()
        .eq("id", brandingId);

      if (error) {
        throw error;
      }
    } catch (error) {
      console.error("Error deleting organization branding:", error);
      throw error;
    }
  }

  /**
   * Upload logo for an organization
   * File naming: {organisationId}.{extension}
   */
  static async uploadOrganizationLogo(
    organizationId: string,
    file: File
  ): Promise<LogoUploadResult> {
    try {
      // Validate file
      const validation = this.validateLogoFile(file);
      if (!validation.valid) {
        return {
          success: false,
          error: validation.error,
        };
      }

      // Get file extension
      const fileExtension = file.name.split(".").pop()?.toLowerCase();
      if (!fileExtension) {
        return {
          success: false,
          error: "Invalid file extension",
        };
      }

      // Create file path: organizationId.extension
      const filePath = `${organizationId}.${fileExtension}`;

      // Delete existing logo first (if any)
      await this.deleteLogoFromStorage(organizationId);

      // Upload new logo
      const { error: uploadError } = await supabase.storage
        .from(BRANDING_BUCKET)
        .upload(filePath, file, {
          cacheControl: "60",
          upsert: true, // This will overwrite existing file
        });

      if (uploadError) {
        throw uploadError;
      }

      // Get public URL
      const { data: urlData } = supabase.storage
        .from(BRANDING_BUCKET)
        .getPublicUrl(filePath);

      return {
        success: true,
        url: urlData.publicUrl,
      };
    } catch (error) {
      console.error("Error uploading organization logo:", error);
      return {
        success: false,
        error: error instanceof Error ? error.message : "Upload failed",
      };
    }
  }

  /**
   * Delete logo from storage for an organization
   */
  static async deleteLogoFromStorage(organizationId: string): Promise<void> {
    try {
      // List all files for this organization (different extensions)
      const { data: files, error: listError } = await supabase.storage
        .from(BRANDING_BUCKET)
        .list("", {
          search: organizationId,
        });

      if (listError) {
        console.error("Error listing files:", listError);
        return;
      }

      if (files && files.length > 0) {
        // Delete all files that match the organization ID pattern
        const filesToDelete = files
          .filter((file) => file.name.startsWith(`${organizationId}.`))
          .map((file) => file.name);

        if (filesToDelete.length > 0) {
          const { error: deleteError } = await supabase.storage
            .from(BRANDING_BUCKET)
            .remove(filesToDelete);

          if (deleteError) {
            console.error("Error deleting logo files:", deleteError);
          }
        }
      }
    } catch (error) {
      console.error("Error in deleteLogoFromStorage:", error);
    }
  }

  /**
   * Create or update branding (upsert operation)
   */
  static async upsertOrganizationBranding(
    organizationId: string,
    brandingData: Partial<CreateBrandingData>
  ): Promise<OrganizationBranding> {
    try {
      console.log(`[BrandingService] 🔄 Upserting branding for organization: ${organizationId}`);

      // Try RPC function first to bypass RLS restrictions
      try {
        const { data, error } = await supabase
          .rpc("update_organization_branding", {
            p_org_id: organizationId,
            p_branding_data: brandingData,
          });

        if (!error && data && data.length > 0) {
          const brandingResult = data[0];
          console.log(`[BrandingService] ✅ Branding updated via RPC successfully`);
          return brandingResult;
        }
      } catch (rpcError: any) {
        console.warn(`[BrandingService] ⚠️ RPC function failed, trying fallback:`, rpcError);
      }

      // Fallback to original approach
      console.log(`[BrandingService] ⚠️ Using fallback approach for branding update`);

      // Check if branding already exists
      const existingBranding = await this.getOrganizationBranding(
        organizationId
      );

      if (existingBranding) {
        // Update existing branding
        return await this.updateOrganizationBranding({
          id: existingBranding.id,
          ...brandingData,
        });
      } else {
        // Create new branding
        return await this.createOrganizationBranding({
          organisation_id: organizationId,
          ...brandingData,
        });
      }
    } catch (error) {
      console.error("Error upserting organization branding:", error);
      throw error;
    }
  }

  /**
   * Validate logo file
   */
  private static validateLogoFile(file: File): {
    valid: boolean;
    error?: string;
  } {
    // Check file size
    if (file.size > MAX_LOGO_SIZE) {
      return {
        valid: false,
        error: `File size must be less than ${MAX_LOGO_SIZE / 1024 / 1024}MB`,
      };
    }

    // Check file type
    if (!ALLOWED_LOGO_TYPES.includes(file.type as any)) {
      return {
        valid: false,
        error: "File must be PNG, JPG, or SVG format",
      };
    }

    return { valid: true };
  }

  /**
   * Get default branding colors
   */
  static getDefaultColors() {
    return {
      primary: BRANDING_CONFIG.COLORS.DEFAULT_PRIMARY,
      secondary: BRANDING_CONFIG.COLORS.DEFAULT_SECONDARY,
      accent: BRANDING_CONFIG.COLORS.DEFAULT_ACCENT,
      font_color: BRANDING_CONFIG.COLORS.DEFAULT_FONT_COLOR,
    };
  }

  /**
   * Get available font families
   */
  static getAvailableFonts() {
    return BRANDING_CONFIG.FONTS;
  }

  /**
   * Validate hex color
   */
  static isValidHexColor(color: string): boolean {
    return /^#[0-9A-Fa-f]{6}$/.test(color);
  }

  /**
   * Validate email
   */
  static isValidEmail(email: string): boolean {
    return /^[^@]+@[^@]+\.[^@]+$/.test(email);
  }

  /**
   * Validate URL
   */
  static isValidUrl(url: string): boolean {
    try {
      new URL(url);
      return url.startsWith("http://") || url.startsWith("https://");
    } catch {
      return false;
    }
  }

  /**
   * Get logo URL with cache busting
   */
  static getLogoUrlWithCacheBusting(logoUrl: string): string {
    if (!logoUrl) return logoUrl;

    const separator = logoUrl.includes("?") ? "&" : "?";
    return `${logoUrl}${separator}t=${Date.now()}`;
  }
}

export default BrandingService;
